/**
 * QuotePreview.jsx — Live quote preview sidebar
 *
 * Updates in real-time as the user builds the quote.
 * Shows: client info, line items, subtotal, discount, total, terms summary.
 */

import { fmtC, fmtD } from '../../lib/utils';
import { buildVirtourDisplayLines } from '../../lib/virtourQuote';

export default function QuotePreview({
  state,
  computed,
  activeTerms = [],
}) {
  const { client, contact, email, phone, address, quoteNumber, quoteDate, validUntil, mode, originator } = state;
  const { activeLines, subtotal, discountAmount, total, estimatedDuration, lineCount } = computed;

  // VirTour: the amount on a row is our payout, not VirTour's retail, so the
  // rows add up to the total the client sees.
  const isVirtour = mode === 'virtour';
  const previewLines = isVirtour ? buildVirtourDisplayLines(activeLines) : activeLines;

  const hasContent = lineCount > 0 || client;

  if (!hasContent) {
    return (
      <div className="bg-surface border border-border rounded-lg p-6 text-center">
        <p className="text-text-dim text-sm">
          Fill in the form to see a live preview
        </p>
      </div>
    );
  }

  const enabledTerms = activeTerms.filter((t) => t.on);

  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden text-sm">
      {/* Header bar */}
      <div className="bg-accent/10 border-b border-accent/20 px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="text-accent font-heading text-xs uppercase tracking-wider">
            Quote Preview
          </span>
          {quoteNumber && (
            <span className="text-text-dim text-xs mono">{quoteNumber}</span>
          )}
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Client info */}
        <div>
          {address && (
            <div className="text-accent font-medium text-base mb-1">{address}</div>
          )}
          {client && <div className="text-text-primary font-medium">{client}</div>}
          {contact && contact !== client && (
            <div className="text-text-dim text-xs">{contact}</div>
          )}
          {email && <div className="text-text-dim text-xs">{email}</div>}
          {phone && <div className="text-text-dim text-xs">{phone}</div>}
        </div>

        {/* Dates */}
        <div className="flex gap-4 text-xs text-text-dim">
          {quoteDate && <span>Date: {fmtD(quoteDate)}</span>}
        </div>

        {/* Line items */}
        {lineCount > 0 && (
          <div>
            <div className="border-t border-border pt-3 mb-2">
              <div className="flex text-2xs text-text-dim uppercase tracking-wider mb-2">
                <span className="flex-1">Service</span>
                <span className="w-16 text-right">Amount</span>
              </div>
            </div>

            <div className="space-y-1.5">
              {previewLines.map((line) => {
                const amount = (line.qty || 0) * (line.rate || 0);
                const isAddon = line.source === 're-addon';
                return (
                  <div
                    key={line.id}
                    className={`flex items-start gap-2 ${isAddon ? 'pl-3 opacity-70' : ''}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-text-primary text-xs truncate">
                        {isAddon && '+ '}{line.desc}
                      </div>
                      {line.vtNote ? (
                        <div className="text-2xs text-text-dim">{line.vtNote}</div>
                      ) : line.qty > 1 && (
                        <div className="text-2xs text-text-dim">
                          {line.qty} x {fmtC(line.rate)}
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-accent font-medium w-16 text-right flex-shrink-0">
                      {fmtC(amount)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Totals */}
        <div className="border-t border-border pt-3 space-y-1.5">
          {isVirtour ? (
            <>
              <div className="flex justify-between text-xs">
                <span className="text-text-dim">VirTour retail</span>
                <span className="text-text-primary">{fmtC(computed.retailTotal)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-text-dim">Blended split</span>
                <span className="text-text-primary">{computed.blendedPct}%</span>
              </div>
            </>
          ) : (
            <div className="flex justify-between text-xs">
              <span className="text-text-dim">Subtotal</span>
              <span className="text-text-primary">{fmtC(subtotal)}</span>
            </div>
          )}

          {discountAmount > 0 && (
            <div className="flex justify-between text-xs">
              <span className="text-text-dim">
                Discount
                {state.discountType === 'pct' ? ` (${state.discountValue}%)` : ''}
              </span>
              <span className="text-green">-{fmtC(discountAmount)}</span>
            </div>
          )}

          <div className="flex justify-between text-sm font-bold pt-1 border-t border-border">
            <span className="text-text-primary">Total</span>
            <span className="text-accent text-base">{fmtC(total)}</span>
          </div>
        </div>

        {/* Duration */}
        {estimatedDuration > 0 && (
          <div className="text-xs text-text-dim">
            Est. duration: {Math.floor(estimatedDuration / 60)}h {estimatedDuration % 60}m
          </div>
        )}

        {/* Terms summary */}
        {enabledTerms.length > 0 && (
          <div className="border-t border-border pt-3">
            <div className="text-2xs text-text-dim uppercase tracking-wider mb-1.5">
              Terms ({enabledTerms.length})
            </div>
            <div className="flex flex-wrap gap-1">
              {enabledTerms.map((t) => (
                <span
                  key={t.id}
                  className="text-2xs px-1.5 py-0.5 rounded bg-surface-2 text-text-dim"
                >
                  {t.label}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Originator */}
        {originator && (
          <div className="text-xs text-text-dim border-t border-border pt-2">
            Originator: {originator}
          </div>
        )}
      </div>
    </div>
  );
}
